<template>
    <div>
        <div v-if="showLayoutIntro">
            <section class="bg-yellow-2 text-green-1 c-hero-page">
                <div class="container-1920-px">
                    <div class="lg:grid grid-cols-12 gap-x-gutter items-center">
                        <div class="col-span-6">
                            <div class="relative">
                                <div class="js-svg-circle-small-3 bg-pink-1 absolute top-[-10%] right-[50%]"></div>
                                <div class="js-svg-circle-small-4 bg-green-1 absolute top-[8%] right-[60%]"></div>
                                <img src="/dist/svg/auto-evaluation.svg" class alt />
                            </div>
                        </div>

                        <div class="col-span-4 col-start-8 text-center mt-md lg:mt-0">
                            <h1 class="h2">{{ introTitle }}</h1>
                            <div class="wysiwyg wysiwyg-editor mt-9" v-html="introBody"></div>
                            <div class="flex justify-center mt-10">
                                <button class="btn-pink" @click.prevent="startQuiz()">{{ introButtonLabel }}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        <div v-if="!showLayoutIntro" class="">
            <div class="py-[40px] xl:py-[200px] container-fluid-px min-h-screen" :class="getBgColor" v-if="loaded" ref="elLayoutQuiz">
                <div class="container-1360">
                    <!-- question-->
                    <div class="flex flex-col xl:flex-row rounded-md bg-yellow-2 py-14 px-10 shadow-md max-w-[600px] mx-auto xl:max-w-none">
                        <!-- column image -->
                        <div class="mx-auto xl:mr-10 3xl:mr-20 w-[200px] xl:w-[530px] relative">
                            <div class="font-bold text-[75px] text-center text-green-1 xl:absolute z-10 xl:top-[15%] xl:left-1/2 xl:-translate-x-1/2"
                                v-if="showLayoutMessageResult">{{ resultPourcentage }}</div>
                            <img class="hidden xl:block" :src="questionCurrent.imageUrl" v-if="!showLayoutMessageResult" alt />
                            <img class="hidden xl:block" :src="showImageResult" v-if="showLayoutMessageResult" alt />
                        </div>

                        <!-- column content -->
                        <div class="flex-1 text-green-1 xl:pt-sm">
                            <!-- showLayoutQuestion -->
                            <div class="flex flex-col h-full max-w-[530px]" v-if="showLayoutQuestion">
                                <div class="wysiwyg wysiwyg-editor text-green-1 font-semibold font-phoreuscherokee mx-auto xl:text-[30px] text-center my-6"
                                    v-html="questionCurrent.questionBody"></div>

                                <!-- choices -->
                                <div class="flex justify-center mt-auto space-x-10">
                                    <button v-for="(choice, index) in questionCurrent.choices" :key="index" class="min-w-0"
                                        :class="index === 0 ? 'btn-pink' : 'btn-yellow'" @click.prevent="questionValidation(index)">{{ choice
                                        }}</button>
                                </div>
                            </div>

                            <!-- showLayoutMessageGoodAnswer -->
                            <div class="flex flex-col h-full max-w-[530px]" v-if="showLayoutMessageGoodAnswer">
                                <div class="h2 text-center">{{ questionCurrent.goodAnswerTitle }}</div>
                                <div class="wysiwyg wysiwyg-editor text-lg my-6" v-html="questionCurrent.goodAnswerBody"></div>

                                <div class="flex justify-center mt-auto">
                                    <button class="btn-pink" @click.prevent="nextQuestion()" v-if="questionIndex < questionsTotal">{{ nextQuestionLabel }}</button>
                                    <button class="btn-pink" @click.prevent="showResult()" v-if="questionIndex >= questionsTotal">{{ resultsLabel }}</button>
                                </div>
                            </div>

                            <!-- showLayoutMessageBadAnswer -->
                            <div class="flex flex-col h-full max-w-[530px]" v-if="showLayoutMessageBadAnswer">
                                <div class="h2 text-center">{{ questionCurrent.badAnswerTitle }}</div>
                                <div class="wysiwyg wysiwyg-editor text-lg my-6" v-html="questionCurrent.badAnswerBody"></div>

                                <div class="flex justify-center mt-auto">
                                    <button class="btn-pink" @click.prevent="nextQuestion()" v-if="questionIndex < questionsTotal">{{ nextQuestionLabel }}</button>
                                    <button class="btn-pink" @click.prevent="showResult()" v-if="questionIndex >= questionsTotal">{{ resultsLabel }}</button>
                                </div>
                            </div>

                            <!-- showLayoutMessageResult -->
                            <div class="flex flex-col h-full max-w-[530px]" v-if="showLayoutMessageResult">
                                <div class="wysiwyg wysiwyg-editor text-lg xl:mt-6 mx-auto mt-sm" v-html="resultMessage"></div>

                                <div class="flex justify-center mt-8">
                                    <button class="btn-pink" @click.prevent="redo()">{{ resultButtonRedoLabel }}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <QuizPagination :total="questionsTotal" :current="(questionIndex - 1)" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, defineProps, nextTick } from 'vue';
import QuizPagination from './QuizPagination.vue'

const props = defineProps({
    quizId: { type: Number, required: true },
    lang: { type: String, required: true },
    introTitle: { type: String, required: true },
    introBody: { type: String, required: true },
    introButtonLabel: { type: String, required: true },
    resultButtonRedoLabel: { type: String, required: true },
    nextQuestionLabel: { type: String, required: true },
    resultsLabel: { type: String, required: true },
})

// Variables
// ==========================================================================
let loaded = ref(false);
let appData = ref(null);
let questionsTotal = ref(null);
let questionIndex = ref(1);
let questionCurrent = ref(null);
let countGoodAnswer = ref(0);
let countGoodAnswerRatio = ref(0);

let showLayoutIntro = ref(true);
let showLayoutQuestion = ref(false);
let showLayoutMessageGoodAnswer = ref(false);
let showLayoutMessageBadAnswer = ref(false);
let showLayoutMessageResult = ref(false);

let elLayoutQuiz = ref(null);

// Methods
// ==========================================================================
const getData = async function () {

    try {
        let response = await fetch(`/api/quiz/search/${props.quizId}/${props.lang}`);
        const data = await response.json();
        appData.value = data.data;
        loaded.value = true;

        questionsTotal.value = appData.value.questions.length;
        questionIndex.value = 1;
        questionCurrent.value = appData.value.questions[(questionIndex.value - 1)];

    } catch (err) {
        console.error(err);
    }
};

const nextQuestion = function () {
    showLayoutMessageGoodAnswer.value = false;
    showLayoutMessageBadAnswer.value = false;
    showLayoutMessageResult.value = false;
    showLayoutQuestion.value = true;

    questionIndex.value = questionIndex.value + 1;
    questionCurrent.value = appData.value.questions[(questionIndex.value - 1)];
}

const showResult = function () {
    showLayoutQuestion.value = false;
    showLayoutMessageGoodAnswer.value = false;
    showLayoutMessageBadAnswer.value = false;
    showLayoutMessageResult.value = true;
}

const questionValidation = function (index) {
    const goodAnswer = parseInt(questionCurrent.value.ChoicesAnswer.value);

    if (index === goodAnswer) {
        countGoodAnswer.value = countGoodAnswer.value + 1;

        showLayoutIntro.value = false;
        showLayoutMessageGoodAnswer.value = true;
        showLayoutMessageBadAnswer.value = false;
        showLayoutMessageResult.value = false;
        showLayoutQuestion.value = false;
    }
    else {
        showLayoutIntro.value = false;
        showLayoutMessageBadAnswer.value = true;
        showLayoutMessageResult.value = false;
        showLayoutQuestion.value = false;
    }

    countGoodAnswerRatio.value = countGoodAnswer.value / questionsTotal.value;
};

const redo = function () {
    questionIndex.value = 1;
    questionCurrent.value = appData.value.questions[(questionIndex.value - 1)];
    showLayoutQuestion.value = true;

    showLayoutMessageGoodAnswer.value = false;
    showLayoutMessageBadAnswer.value = false;
    showLayoutMessageResult.value = false;
}

const startQuiz = async function () {
    showLayoutIntro.value = false;
    showLayoutQuestion.value = true;

    // DOM is now updated
    await nextTick();
    elLayoutQuiz.value.scrollIntoView({ behavior: "smooth" });
}

// Computed
// ==========================================================================
const getBgColor = computed(() => {

    let cssClass = 'bg-yellow-1';

    switch (questionCurrent.style) {
        case 'green':
            cssClass = 'bg-green-1';
            break;

        case 'pink':
            cssClass = 'bg-pink-1';
            break;
    }

    return cssClass
});

const resultPourcentage = computed(() => {
    return parseInt((countGoodAnswerRatio.value * 100)) + ' %';
});

const showImageResult = computed(() => {
    return countGoodAnswerRatio >= 0.6 ? appData.value.quizGoodResultImage : appData.value.quizBadResultImage;
});

const resultMessage = computed(() => {

    console.log('countGoodAnswerRatio', countGoodAnswerRatio.value)

    if (countGoodAnswerRatio.value >= 1) {
        return appData.value.quizResult100p;
    }

    if (countGoodAnswerRatio.value <= 0.93 && countGoodAnswerRatio.value >= 0.8) {
        return appData.value.quizResult80p93p;
    }

    if (countGoodAnswerRatio.value <= 0.79 && countGoodAnswerRatio.value >= 0.53) {
        return appData.value.quizResult53p79p;
    }

    if (countGoodAnswerRatio.value <= 0.52 && countGoodAnswerRatio.value >= 0.2) {
        return appData.value.quizResult20p52p;
    }

    return appData.value.quizResult0p19p;
});

// onMounted
// ==========================================================================
onMounted(() => {
    getData();
});
</script>
